/**
 * TopAppBar — WebGIS command bar
 *
 * Layout (Apple / ArcGIS Pro style):
 *   [Brand] | [scrollable map tools…]     [Mode pill] [API key] [theme] [account]
 *
 * Right-side controls are fixed width, never crushed by the tool strip.
 */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  ButtonBase,
  Button,
} from '@mui/material';
import {
  LightMode,
  DarkMode,
  AccountCircle,
  Logout,
  Help,
  Public,
  KeyboardArrowDown,
  AutoAwesome,
  Tune,
  Key,
  AdminPanelSettings,
  Dashboard as DashboardIcon,
  Layers,
  Add,
  FolderOpen,
  Save,
  Straighten,
  Search,
  TableChart,
  Map as MapIcon,
  Brush,
  Edit,
  InfoOutlined,
  Print,
  Calculate,
  Palette,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../theme';
import { useGeoveraTheme } from '../../theme/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { clearSessionToken } from '../../utils/sessionManager';
import { setNonTechnicalMode, addNotification } from '../../store/slices/uiSlice';
import { LogoIcon } from '../Logo';

const ToolBtn: React.FC<{
  title: string;
  onClick: () => void;
  children: React.ReactNode;
  active?: boolean;
}> = ({ title, onClick, children, active }) => {
  const { palette } = useGeoveraTheme();
  return (
    <Tooltip title={title} arrow>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={title}
        sx={{
          width: 30,
          height: 30,
          borderRadius: tokens.radius.md,
          color: active ? palette.accent.primary : palette.text.secondary,
          bgcolor: active ? palette.accent.primaryDim : 'transparent',
          border: `1px solid ${active ? palette.accent.primary : 'transparent'}`,
          '&:hover': {
            backgroundColor: palette.accent.primaryDim,
            color: palette.accent.primary,
          },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
};

const ToolGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { palette } = useGeoveraTheme();
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.1,
        px: 0.35,
        py: 0.15,
        borderRadius: tokens.radius.lg,
        bgcolor: palette.bg.elevated,
        border: `1px solid ${palette.border.subtle}`,
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
};

const TopAppBar: React.FC = () => {
  const { mode, toggleMode, palette } = useGeoveraTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);
  const isNonTechnical = useAppSelector((s) => s.ui.isNonTechnicalMode);
  const layerCount = useAppSelector((s) => s.layers.layers.length);

  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);

  const handleUserMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(e.currentTarget);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    setUserMenuAnchor(null);
  }, []);

  const handleLogout = useCallback(() => {
    handleUserMenuClose();
    clearSessionToken();
    dispatch(logout());
    navigate('/login');
  }, [dispatch, handleUserMenuClose, navigate]);

  const fire = useCallback((name: string, detail?: unknown) => {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }, []);

  const openActivity = useCallback((id: string) => fire('geovera:open-activity', { id }), [fire]);

  const setMode = useCallback(
    (simple: boolean) => {
      if (simple === isNonTechnical) return;
      dispatch(setNonTechnicalMode(simple));
      dispatch(
        addNotification({
          type: 'info',
          message: simple
            ? 'Simple mode — guided everyday tasks'
            : 'Advanced mode — full WebGIS tools',
        })
      );
    },
    [dispatch, isNonTechnical]
  );

  const userInitial = user?.email?.[0]?.toUpperCase() || 'U';
  const userName = user?.username || user?.email?.split('@')[0] || 'User';
  const isAdmin = user?.role === 'admin';
  const isAdvanced = !isNonTechnical;

  return (
    <Box
      sx={{
        height: tokens.layout.headerHeight,
        minHeight: tokens.layout.headerHeight,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        // Even padding; brand has extra clearance before tools
        px: { xs: 1.25, sm: 1.75 },
        gap: 0,
        backgroundColor: palette.bg.raised,
        borderBottom: `1px solid ${palette.border.base}`,
        zIndex: 10,
        overflow: 'hidden',
      }}
    >
      {/* ── Brand ─────────────────────────────────────────── */}
      <Box
        onClick={() => navigate('/dashboard')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          flexShrink: 0,
          // Breathing room after logo before tools
          pr: { xs: 2, sm: 2.75, md: 3.25 },
          mr: { xs: 1.5, sm: 2 },
          borderRight: `1px solid ${palette.border.base}`,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: 'rgba(15, 23, 42, 0.8)',
            border: '1.5px solid rgba(79, 195, 247, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 10px rgba(79, 195, 247, 0.2)',
          }}
        >
          <LogoIcon size={20} />
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', lineHeight: 1 }}>
          <Typography
            sx={{
              color: palette.text.primary,
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            GeoVera
          </Typography>
          <Typography
            sx={{
              color: palette.text.muted,
              fontSize: 9,
              letterSpacing: '0.7px',
              textTransform: 'uppercase',
              lineHeight: 1.15,
            }}
          >
            WebGIS
          </Typography>
        </Box>
      </Box>

      {/* ── Map tools + mode/API — content-width only (no empty flex void) ─ */}
      <Box
        sx={{
          // Shrink if needed, never grow into empty dead space after tools
          flex: '0 1 auto',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.65, sm: 0.85 },
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          py: 0.25,
          pl: { xs: 0.25, sm: 0.5 },
        }}
      >
        {isAdvanced && (
          <>
            <ToolGroup>
              <ToolBtn title="Add data" onClick={() => fire('geovera:open-import')}>
                <Add sx={{ fontSize: 17 }} />
              </ToolBtn>
              <ToolBtn title="Layers" onClick={() => openActivity('layers')}>
                <Layers sx={{ fontSize: 16 }} />
              </ToolBtn>
              <ToolBtn title="Basemap" onClick={() => fire('geovera:toggle-basemap')}>
                <MapIcon sx={{ fontSize: 16 }} />
              </ToolBtn>
              <ToolBtn title="Symbology" onClick={() => openActivity('symbology')}>
                <Palette sx={{ fontSize: 16 }} />
              </ToolBtn>
            </ToolGroup>

            <ToolGroup>
              <ToolBtn title="Identify" onClick={() => fire('geovera:open-identify')}>
                <InfoOutlined sx={{ fontSize: 16 }} />
              </ToolBtn>
              <ToolBtn title="Attribute table" onClick={() => fire('geovera:open-attributes')}>
                <TableChart sx={{ fontSize: 16 }} />
              </ToolBtn>
              <ToolBtn title="Search" onClick={() => openActivity('search')}>
                <Search sx={{ fontSize: 16 }} />
              </ToolBtn>
            </ToolGroup>

            <ToolGroup>
              <ToolBtn title="Draw" onClick={() => openActivity('drawing')}>
                <Brush sx={{ fontSize: 16 }} />
              </ToolBtn>
              <ToolBtn title="Edit" onClick={() => openActivity('edit')}>
                <Edit sx={{ fontSize: 16 }} />
              </ToolBtn>
              <ToolBtn title="Measure" onClick={() => openActivity('measure')}>
                <Straighten sx={{ fontSize: 16 }} />
              </ToolBtn>
              <ToolBtn title="Analysis" onClick={() => openActivity('analysis')}>
                <Calculate sx={{ fontSize: 16 }} />
              </ToolBtn>
            </ToolGroup>

            <ToolGroup>
              <ToolBtn
                title="Open project"
                onClick={() => fire('geovera:open-project', { mode: 'open' })}
              >
                <FolderOpen sx={{ fontSize: 16 }} />
              </ToolBtn>
              <ToolBtn
                title="Save project"
                onClick={() => fire('geovera:open-project', { mode: 'save' })}
              >
                <Save sx={{ fontSize: 16 }} />
              </ToolBtn>
              <ToolBtn title="Print & export" onClick={() => openActivity('print')}>
                <Print sx={{ fontSize: 16 }} />
              </ToolBtn>
            </ToolGroup>

            <Tooltip title={`${layerCount} layer(s)`} arrow>
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  gap: 0.4,
                  px: 0.85,
                  height: 28,
                  borderRadius: tokens.radius.md,
                  color: palette.text.muted,
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                <Layers sx={{ fontSize: 13 }} />
                {layerCount}
              </Box>
            </Tooltip>
          </>
        )}

        {/* Tight gap: mode + API sit right after tools (no huge void) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
            ml: { xs: 0.5, sm: 1 },
            pl: { xs: 0.75, sm: 1.25 },
            borderLeft: `1px solid ${palette.border.subtle}`,
          }}
        >
          {/* Segmented mode control — always labeled, fixed size */}
          <Tooltip
            title={
              isAdvanced
                ? 'You are in Advanced mode (full WebGIS). Click Simple for guided tasks.'
                : 'You are in Simple mode. Click Advanced for full GIS tools.'
            }
            arrow
          >
            <Box
              role="group"
              aria-label="Workspace mode"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 30,
                p: '2px',
                borderRadius: 999,
                bgcolor: palette.bg.elevated,
                border: `1px solid ${palette.border.base}`,
                flexShrink: 0,
              }}
            >
              <ButtonBase
                onClick={() => setMode(false)}
                aria-pressed={isAdvanced}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                  height: 26,
                  px: { xs: 0.9, sm: 1.15 },
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  whiteSpace: 'nowrap',
                  color: isAdvanced ? '#fff' : palette.text.muted,
                  bgcolor: isAdvanced ? palette.accent.primary : 'transparent',
                  boxShadow: isAdvanced ? `0 1px 4px ${palette.accent.primary}55` : 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: isAdvanced ? palette.accent.primary : palette.bg.highest,
                  },
                }}
              >
                <Tune sx={{ fontSize: 13 }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Advanced
                </Box>
              </ButtonBase>
              <ButtonBase
                onClick={() => setMode(true)}
                aria-pressed={!isAdvanced}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                  height: 26,
                  px: { xs: 0.9, sm: 1.15 },
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  whiteSpace: 'nowrap',
                  color: !isAdvanced ? '#0b1a12' : palette.text.muted,
                  bgcolor: !isAdvanced ? '#81c784' : 'transparent',
                  boxShadow: !isAdvanced ? '0 1px 4px rgba(129,199,132,0.4)' : 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: !isAdvanced ? '#66bb6a' : palette.bg.highest,
                  },
                }}
              >
                <AutoAwesome sx={{ fontSize: 13 }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Simply Map
                </Box>
              </ButtonBase>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Theme + account — pinned right, only residual free space is here */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          ml: 'auto',
          pl: 1.5,
        }}
      >
        <Tooltip title={mode === 'dark' ? 'Light theme' : 'Dark theme'} arrow>
          <IconButton
            size="small"
            onClick={toggleMode}
            aria-label="Toggle theme"
            sx={{
              width: 30,
              height: 30,
              color: palette.text.secondary,
              flexShrink: 0,
              '&:hover': { color: palette.accent.primary, bgcolor: palette.accent.primaryDim },
            }}
          >
            {mode === 'dark' ? (
              <LightMode sx={{ fontSize: 16 }} />
            ) : (
              <DarkMode sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>

        <ButtonBase
          onClick={handleUserMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            height: 30,
            pl: 0.35,
            pr: { xs: 0.35, md: 0.75 },
            borderRadius: tokens.radius.md,
            color: palette.text.primary,
            flexShrink: 0,
            '&:hover': { bgcolor: palette.bg.highest },
          }}
          aria-label="Account menu"
        >
          <Avatar
            sx={{
              width: 24,
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 700,
              bgcolor: palette.accent.primary,
              color: '#fff',
            }}
          >
            {userInitial}
          </Avatar>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              display: { xs: 'none', lg: 'block' },
              maxWidth: 88,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {userName}
          </Typography>
          <KeyboardArrowDown
            sx={{
              fontSize: 15,
              color: palette.text.muted,
              display: { xs: 'none', md: 'block' },
            }}
          />
        </ButtonBase>
      </Box>

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 230,
            mt: 0.75,
            border: `1px solid ${palette.border.base}`,
            bgcolor: palette.bg.elevated,
          },
        }}
      >
        {user?.email && (
          <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${palette.border.subtle}` }}>
            <Typography sx={{ fontSize: tokens.fontSize.md, fontWeight: 700 }}>
              {userName}
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.sm, color: palette.text.muted }}>
              {user.email}
            </Typography>
          </Box>
        )}
        <MenuItem
          onClick={() => {
            handleUserMenuClose();
            dispatch(setNonTechnicalMode(false));
            navigate('/dashboard');
          }}
        >
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>WebGIS workspace</ListItemText>
        </MenuItem>

        {isAdmin && (
          <MenuItem
            onClick={() => {
              handleUserMenuClose();
              navigate('/admin/api-keys');
            }}
          >
            <ListItemIcon>
              <AdminPanelSettings fontSize="small" />
            </ListItemIcon>
            <ListItemText>Admin · service requests</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleUserMenuClose();
            window.open('/digipin-encoder', '_blank', 'noopener,noreferrer');
          }}
        >
          <ListItemIcon>
            <Help fontSize="small" />
          </ListItemIcon>
          <ListItemText>DIGIPIN help</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleUserMenuClose();
            navigate('/profile');
          }}
        >
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TopAppBar;
